// ─── blocagesParcours.js ─────────────────────────────────────────────
// Pourquoi Anissa ne peut pas avancer, dit en clair.
//
// POURQUOI CE FICHIER
//
// Le système sait déjà pourquoi il refuse. Il le calcule partout : dans le
// pied de publication, à la validation du plan, dans les garde-fous
// cliniques. Mais chaque refus vit là où il est calculé, souvent loin du
// bouton, parfois sans un mot.
//
// Cette semaine, une publication a tourné en boucle pendant deux jours parce
// que le refus « Parcours ou anamnèse non confirmés » s'affichait trois cents
// pixels sous le bouton qui l'avait provoqué.
//
// Ici on rassemble ce que le code sait déjà, en un seul endroit, avec pour
// chaque point ce qu'il faut faire. Rien n'est deviné ni généré : tout est
// CALCULÉ à partir de l'état réel. Un texte calculé est toujours juste.
//
// ⚠ On ne propose JAMAIS de contourner un verrou clinique. Ces verrous
// existent pour arrêter Anissa quand quelque chose manque ; les aider à
// sauter serait pire que pas d'aide du tout. On dit ce qui manque, elle
// décide.

import { canValidateAnamnesisStep, hasAnamnesisCompletionProof } from './journeyState';
import { isAnamneseFilled } from './anamneseAnalyzer';
import { hasRiskProfileAssessment } from './riskProfileFlags';
import { getPackAccess } from './packSystem';

/** Un point à régler, ou déjà réglé. */
const point = (ok, titre, quoiFaire = null, ou = null) => Object.freeze({
  ok, titre, quoiFaire, ou,
});

function emailDe(client) {
  return String(client?.form?.email || client?.email || '').trim();
}

/**
 * Points communs à tout le parcours : sans eux, rien n'avance nulle part.
 */
function socle(client) {
  const points = [];
  const pack = getPackAccess(client?.packType || client?.pack_type || '');

  points.push(pack.known
    ? point(true, 'Le pack de la cliente est reconnu')
    : point(false, 'Le pack de la cliente est absent ou inconnu',
      'Choisis son pack dans sa fiche', 'profil'));

  points.push(emailDe(client)
    ? point(true, 'La cliente a une adresse e-mail')
    : point(false, 'La cliente n’a pas d’adresse e-mail',
      'Ajoute son adresse dans sa fiche', 'profil'));

  return points;
}

/**
 * Points propres à l'étape en cours.
 *
 * On ne couvre que ce qu'on sait calculer honnêtement depuis l'état de la
 * fiche. Un écran qui affirmerait « tout va bien » alors qu'il n'a pas
 * regardé serait pire que pas d'écran.
 */
function pourEtape(client, journey, etape) {
  const form = client?.form || {};
  const points = [];

  if (etape === 'anamnesis') {
    points.push(isAnamneseFilled(form)
      ? point(true, 'L’anamnèse est remplie')
      : point(false, 'L’anamnèse n’est pas complète',
        'Reprends le questionnaire avec elle', 'anamnesis'));

    points.push(hasAnamnesisCompletionProof(journey)
      ? point(true, 'L’anamnèse est confirmée')
      : point(false, 'L’anamnèse n’a pas encore été confirmée',
        'Confirme-la en bas de l’étape', 'anamnesis'));
    return points;
  }

  if (etape === 'plan_generation' || etape === 'plan_editing') {
    points.push(canValidateAnamnesisStep(form, journey)
      ? point(true, 'L’anamnèse est complète et confirmée')
      : point(false, 'L’anamnèse est incomplète ou non confirmée',
        'Retourne à l’étape Anamnèse', 'anamnesis'));

    // Fail-closed : ce verrou arrête la génération tant qu'Anissa n'a pas
    // tranché sur les profils à risque. On le nomme, on ne le contourne pas.
    points.push(hasRiskProfileAssessment(form)
      ? point(true, 'Les profils à risque sont évalués')
      : point(false, 'Les profils à risque ne sont pas évalués',
        'Réponds aux questions de sécurité dans l’anamnèse', 'anamnesis'));

    points.push(journey?.plan_generated || journey?.external_synthesis_ready
      ? point(true, 'Un plan existe')
      : point(false, 'Aucun plan n’a encore été généré',
        'Génère le plan, ou importe un dossier externe', 'plan_generation'));
    return points;
  }

  if (etape === 'delivery') {
    points.push(journey?.plan_validated
      ? point(true, 'Le plan est validé')
      : point(false, 'Le plan n’est pas validé',
        'Valide-le à l’étape Plan', 'plan_editing'));

    // Le cas qui a coûté deux soirées : la preuve scelle AUSSI l'anamnèse,
    // donc la modifier après validation périme la preuve, sans que le lien
    // entre les deux soit visible nulle part.
    const valideLe = Date.parse(journey?.plan_validation?.validated_at || '');
    const modifieeLe = Date.parse(client?.updatedAt || client?.updated_at || '');
    const preuvePerimee = Number.isFinite(valideLe) && Number.isFinite(modifieeLe)
      && modifieeLe > valideLe;
    if (preuvePerimee) {
      points.push(point(false,
        'La fiche a été modifiée après la validation du plan',
        'La preuve scelle aussi l’anamnèse : reclique « Revalider le contenu de l’app »',
        'delivery'));
    }
    return points;
  }

  if (etape === 'followup') {
    points.push(journey?.delivered
      ? point(true, 'Le programme a été remis')
      : point(false, 'La remise n’a pas été confirmée',
        'Retourne à l’étape Livraison', 'delivery'));
    return points;
  }

  return points;
}

/**
 * Ce qui empêche d'avancer, et ce qui est déjà en place.
 *
 * @param {object} client   - la fiche cliente
 * @param {object} journey  - client.journey_state
 * @param {string} etape    - étape regardée
 * @returns {{
 *   bloquants: Array<object>,
 *   acquis: Array<object>,
 *   couvert: boolean,
 * }}
 */
export function diagnostiquerBlocages(client, journey, etape) {
  const tous = [...socle(client), ...pourEtape(client, journey, etape)];
  return Object.freeze({
    bloquants: Object.freeze(tous.filter((p) => !p.ok)),
    acquis: Object.freeze(tous.filter((p) => p.ok)),
    // Faux quand on ne sait rien dire de cette étape. L'écran l'annoncera
    // plutôt que d'afficher un « tout va bien » qu'il n'a pas vérifié.
    couvert: pourEtape(client, journey, etape).length > 0,
  });
}
